import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { CheckCircle2, Loader2, RefreshCw, FileSignature } from 'lucide-react';

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

export default function SignConvention() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [name, setName] = useState('');
  const [role, setRole] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [signedOk, setSignedOk] = useState(false);
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const load = async () => {
    try {
      const r = await axios.get(`${API_BASE}/prospects/convention/sign/${token}`);
      setState({ loading: false, data: r.data, error: null });
      if (r.data.signed) setSignedOk(true);
    } catch (e) {
      setState({ loading: false, data: null, error: e?.response?.data?.detail || 'Lien invalide' });
    }
  };

  // Canvas drawing
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = c.getBoundingClientRect();
    c.width = rect.width * dpr;
    c.height = rect.height * dpr;
    const ctx = c.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#0f172a';
    ctx.lineWidth = 2;
  }, [state.data]);

  const getPos = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const cx = (e.touches?.[0]?.clientX ?? e.clientX) - rect.left;
    const cy = (e.touches?.[0]?.clientY ?? e.clientY) - rect.top;
    return { x: cx, y: cy };
  };
  const startDraw = (e) => { e.preventDefault(); drawing.current = true; last.current = getPos(e); };
  const draw = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const c = canvasRef.current;
    const ctx = c.getContext('2d');
    const { x, y } = getPos(e);
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(x, y);
    ctx.stroke();
    last.current = { x, y };
  };
  const stopDraw = () => { drawing.current = false; };
  const clearSig = () => {
    const c = canvasRef.current;
    const ctx = c.getContext('2d');
    ctx.clearRect(0, 0, c.width, c.height);
  };
  const isCanvasEmpty = () => {
    const c = canvasRef.current;
    if (!c) return true;
    const ctx = c.getContext('2d');
    const data = ctx.getImageData(0, 0, c.width, c.height).data;
    for (let i = 3; i < data.length; i += 4) { if (data[i] !== 0) return false; }
    return true;
  };

  const submit = async () => {
    if (!name.trim() || !role.trim()) { toast.error('Renseignez votre nom et votre qualité'); return; }
    if (isCanvasEmpty()) { toast.error('Apposez votre signature'); return; }
    setSubmitting(true);
    try {
      const dataUrl = canvasRef.current.toDataURL('image/png');
      const r = await axios.post(`${API_BASE}/prospects/convention/sign/${token}/sign`, {
        signature_data_url: dataUrl,
        signataire_nom: name.trim(),
        signataire_qualite: role.trim(),
      });
      if (r.data?.success) {
        setSignedOk(true);
        toast.success('Convention signée avec succès', { duration: 8000 });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec de la signature', { duration: 12000 });
    }
    setSubmitting(false);
  };

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }
  if (state.error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8 max-w-md text-center">
          <p className="text-rose-600 font-bold">Lien invalide</p>
          <p className="text-slate-500 text-sm mt-2">{state.error}</p>
        </div>
      </div>
    );
  }

  if (signedOk) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-teal-50 p-6">
        <div className="bg-white rounded-2xl shadow-xl border border-emerald-200 p-10 max-w-md text-center">
          <CheckCircle2 className="w-16 h-16 text-emerald-600 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900">Convention signée</h1>
          <p className="text-slate-600 mt-3">
            Merci ! Votre signature a bien été enregistrée. Une copie signée vous est envoyée par email.
          </p>
          <p className="text-xs text-slate-400 mt-6">Reno Resolve · gestion.renoresolve.com</p>
        </div>
      </div>
    );
  }

  const prospect = state.data?.prospect || {};
  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-3xl mx-auto space-y-4">
        <div className="bg-gradient-to-br from-[#003580] to-[#004AAD] rounded-2xl p-6 text-white">
          <div className="flex items-center gap-3">
            <FileSignature className="w-8 h-8" />
            <div>
              <h1 className="text-xl font-bold">{"Convention apporteur d'affaires"}</h1>
              <p className="text-sm opacity-90">Signature électronique — Reno Resolve × {prospect.nom_societe}</p>
            </div>
          </div>
        </div>

        {/* Prospect summary */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <h2 className="font-bold text-slate-900 mb-3">Informations apporteur</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
            <p><span className="text-slate-500">Société : </span><b>{prospect.nom_societe}</b></p>
            {prospect.siret && <p><span className="text-slate-500">SIRET : </span><span className="font-mono">{prospect.siret}</span></p>}
            {(prospect.adresse || prospect.ville) && (
              <p className="md:col-span-2"><span className="text-slate-500">Adresse : </span>
                {[prospect.adresse, [prospect.code_postal, prospect.ville].filter(Boolean).join(' ')].filter(Boolean).join(' · ')}
              </p>
            )}
            {prospect.email && <p><span className="text-slate-500">Email : </span>{prospect.email}</p>}
          </div>
        </div>

        {/* PDF preview */}
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="p-3 border-b border-slate-200 bg-slate-50 text-sm font-semibold text-slate-700">Document à signer</div>
          <iframe
            title="Convention"
            src={`${API_BASE}/prospects/convention/sign/${token}/preview`}
            className="w-full h-[500px] border-0"
            data-testid="convention-preview"
          />
        </div>

        {/* Signature pad */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <h2 className="font-bold text-slate-900 mb-1">Votre signature</h2>
          <p className="text-xs text-slate-500 mb-3">Signez dans la zone ci-dessous avec votre souris ou votre doigt.</p>

          <div className="border-2 border-dashed border-slate-300 rounded-lg bg-slate-50">
            <canvas
              ref={canvasRef}
              data-testid="signature-canvas"
              className="w-full h-44 touch-none cursor-crosshair"
              onMouseDown={startDraw} onMouseMove={draw} onMouseUp={stopDraw} onMouseLeave={stopDraw}
              onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={stopDraw}
            />
          </div>
          <div className="flex justify-between items-center mt-2">
            <Button variant="ghost" size="sm" onClick={clearSig} className="text-slate-500" data-testid="sig-clear">
              <RefreshCw className="w-3.5 h-3.5 mr-1" /> Effacer
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
            <div>
              <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Nom du signataire *</label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="ex : Marc Dupont" data-testid="sig-nom" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Qualité *</label>
              <Input value={role} onChange={(e) => setRole(e.target.value)} placeholder="ex : Gérant, Notaire, Directeur…" data-testid="sig-qualite" />
            </div>
          </div>

          <p className="text-[11px] text-slate-500 mt-4">
            {"En signant, vous reconnaissez avoir pris connaissance du document et acceptez les conditions de la convention apporteur d'affaires. La date, l'heure et votre adresse IP sont enregistrées comme preuve."}
          </p>

          <Button
            onClick={submit}
            disabled={submitting}
            className="w-full mt-4 bg-[#003580] hover:bg-[#002a66] text-white py-6 text-base"
            data-testid="sig-submit"
          >
            {submitting ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <FileSignature className="w-5 h-5 mr-2" />}
            Signer la convention
          </Button>
        </div>
      </div>
    </div>
  );
}
